import {
  afterRenderEffect,
  booleanAttribute,
  Component,
  computed,
  input,
  linkedSignal,
  signal,
  viewChild,
} from '@angular/core';
import { TranslocoDirective } from '@jsverse/transloco';
import { CdkConnectedOverlay } from '@angular/cdk/overlay';
import { Combobox, ComboboxPopup, ComboboxWidget } from '@angular/aria/combobox';
import { Listbox, Option } from '@angular/aria/listbox';
import { JunoError } from '../juno-error/juno-error';
import { FormValueClassAbstract } from '../../../abstract/form-value-class-abstract';
import { MatIcon } from '@angular/material/icon';
import { TJunoSelectOption } from '@dss-libs/shared/type/common-types';

/**
 * An accessible, keyboard-navigable combobox / listbox select built on the
 * `@angular/aria` primitives and integrated with Angular Signal Forms.
 *
 * Supports both **single** and **multi** selection modes. In single mode the
 * bound `value` is a plain `string`; in multi mode it is a `string[]`. The
 * component reconciles these two shapes internally so the parent form field
 * always receives the value in the shape it originally provided.
 *
 * Validation errors are forwarded to `<juno-error>` via
 * {@link FormValueClassAbstract} and rendered below the trigger button.
 *
 * ---
 *
 * @template T  The value type is `string | string[]`, depending on `isMultiple`.
 *              Inherited from
 *              `InputValueClassAbstract<string | string[], { selectedClasses: string; optionClasses: string }>`.
 *
 * ---
 *
 * ### Inputs (own)
 *
 * | Input                | Type                          | Default | Description |
 * |----------------------|-------------------------------|---------|-------------|
 * | `options`            | `TJunoSelectOption<string>[]` | `[]`    | The list of options to render in the dropdown. Each option has `label`, `value`, and an optional `icon` config. |
 * | `isMultiple`         | `boolean` (booleanAttribute)  | `false` | Enables multi-selection. Pass as an attribute flag (`isMultiple`) or `[isMultiple]="true"`. |
 * | `maxVisibleSelections` | `number`                    | `3`     | Maximum number of selected-chip labels shown in the trigger before an overflow badge (`+N`) is displayed. |
 *
 * ### Inputs (inherited from {@link FormValueClassAbstract})
 *
 * `value`, `label`, `labelClass`, `placeholder`, `name`, `appliedClasses`
 * (`{ selectedClasses, optionClasses }`), `required`, `pattern`, `errors`,
 * `pending`, `touched`, `dirty`, `disabled`.
 *
 * ### Outputs (inherited)
 *
 * `touch` – emitted when the popup closes so Signal Forms marks the field touched.
 *
 * ---
 *
 * @usageNotes
 *
 * #### Single-select with Signal Forms (`[formField]`)
 * ```html
 * <juno-libs-ui-select
 *   [formField]="userForm.status"
 *   [options]="statusOptions()"
 *   label="SETTINGS.STATUS"
 *   placeholder="Please select option"
 *   [appliedClasses]="{ selectedClasses: 'text-red-500!', optionClasses: 'text-teal-500!' }"
 * />
 * ```
 *
 * #### Multi-select with Signal Forms
 * ```html
 * <juno-libs-ui-select
 *   [formField]="userForm.roles"
 *   [options]="roleOptions()"
 *   isMultiple
 *   label="SETTINGS.ROLE"
 *   placeholder="Please select option"
 *   [appliedClasses]="{ selectedClasses: 'text-red-500!', optionClasses: 'text-teal-500!' }"
 * />
 * ```
 *
 * #### Standalone two-way binding
 * ```html
 * <juno-libs-ui-select
 *   [(value)]="selectedRole"
 *   [options]="roleOptions()"
 *   label="SETTINGS.ROLE"
 * />
 * ```
 *
 * ---
 *
 * ### Implementation notes
 *
 * - The internal `@angular/aria` `<Listbox>` always emits `string[]`, even in
 *   single mode. `onListboxValueChange` normalises this back to a bare `string`
 *   when the current value is not an array.
 * - `listboxValue` is a `linkedSignal` that derives from `value()`. It cannot
 *   be replaced by a two-way `[(value)]` on the listbox until Angular ships
 *   the `linkedSignal.set()` API (targeted for v22.1), which would eliminate
 *   the sync loop.
 * - `afterRenderEffect` is used to scroll the active item into view on every
 *   render because `activedescendant` navigation never moves DOM focus.
 *
 * @see {@link FormValueClassAbstract} for the full list of inherited inputs/outputs.
 * @see {@link JunoInput} for the text-input counterpart.
 */
@Component({
  selector: 'juno-libs-ui-select',
  imports: [
    JunoError,
    TranslocoDirective,
    CdkConnectedOverlay,
    Combobox,
    ComboboxPopup,
    ComboboxWidget,
    Listbox,
    Option,
    MatIcon,
  ],
  templateUrl: './juno-select.html',
  styleUrl: './juno-select.css',
  host: {
    class: 'juno-select',
  },
})
export class JunoSelect extends FormValueClassAbstract<
  string | string[],
  { selectedClasses: string; optionClasses: string }
> {
  options = input<TJunoSelectOption<string>[]>([]);
  isMultiple = input<boolean>(false, {
    transform: booleanAttribute,
  });

  protected readonly listbox = viewChild(Listbox, { debugName: 'juno-libs-ui-select-listbox' });
  protected readonly popupExpanded = signal(false);
  protected readonly maxVisibleSelections = input<number>(3);

  /** The listbox always works with an array; the control value is a bare string in single mode.
   *  When Angular v 22.1 is released we can go up to this version of angular
   *  it will contain the new Set method of the linkedSignal where we can modify
   *  the source from the linkedSignal itself with no Infinite loop
   *  and we can remove the `onListboxValueChange` function at this stage
   *  and 2 way bind on the`[(value)]="listboxValue"`
   * */
  protected readonly listboxValue = linkedSignal<string[]>(() => {
    const value = this.value();
    if (!value) {
      return [];
    }
    return Array.isArray(value) ? value : [value];
  });

  protected readonly selectedOptions = computed(() =>
    this.options().filter((option) => this.listboxValue().includes(option.value))
  );

  protected readonly visibleSelectedOptions = computed(() =>
    this.selectedOptions().slice(0, this.maxVisibleSelections())
  );

  protected readonly overflowCount = computed(() => this.selectedOptions().length - this.maxVisibleSelections());

  // `activedescendant` navigation never moves DOM focus, so nothing scrolls the popup for us.
  activateDescendantEffect = afterRenderEffect({
    earlyRead: () => this.listbox(),
    write: (list) => {
      list()?.scrollActiveItemIntoView();
    },
  });

  protected onExpandedChange(isExpanded: boolean): void {
    this.popupExpanded.set(isExpanded);
    if (!isExpanded) {
      this.touch.emit();
    }
  }

  /**
   * values is always a string[] because that's what the aria listbox emits, even in single mode.
   * But value on this component can be either a string or a string[]
   * Writes the selection back in the shape the bound [formField] expects.
   *
   * The listbox always emits `string[]`; `value` may be a `string` or a `string[]`, so the current
   * value picks the shape (`isMultiple` only when there is no value yet to read).
   *
   * Empty falls back to `''`, never `undefined`: signal forms drops a field whose value is `undefined`,
   * and the listbox deselects before it selects, so re-picking emits a transient `[]`.
   */
  protected onListboxValueChange(values: string[]): void {
    const current = this.value() ?? (this.isMultiple() ? [] : '');
    this.value.set(Array.isArray(current) ? values : values[0] ?? '');
    if (!this.isMultiple()) {
      this.onExpandedChange(false);
    }
  }
}
